import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ToastContainer } from '@montugroup/design-system';
import alternaleaf from '@montugroup/themes/brands/alternaleaf';
import { BrandThemeProvider } from '@montugroup/themes/styles';
import { CircularProgress, Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';

import './api/pmsAxios';
import { AppRoutes } from './components/AppRoutes';
import settings from './data/constants';
import { getUser } from './data/service/authService';
import useTrackingProviders from './hooks/useTrackingProviders';
import { LaunchDarkly } from './utils/launchdarkly';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import ModalProvider from 'mui-modal-provider';
import { AppSideEffects } from './components/AppSideEffects';
import { useConsoleErrorPhoneOverride } from './error-handling/useConsoleErrorPhoneOverride';
import { TsrReactQueryProviders } from './providers/tsr-react-query-providers';
import { TourProvider } from './tours/TourProvider';
import { generateQueryConfig } from './utils/generateQueryConfig';

const App = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const user = getUser();
  //Hook responsible for removing Patient phone no from the Console error logs
  useConsoleErrorPhoneOverride();

  // Hook responsible for setting up all our tracking services. This can be expanded to include multiple services
  useTrackingProviders(settings);

  // Set up datadogRum user, so we can find users by userID in datadog
  // Safest and fastest implementation to solve for now
  if (
    user &&
    user.constructor === Object &&
    user.id !== undefined &&
    user.id !== null &&
    ['string', 'number'].includes(typeof user.id)
  ) {
    try {
      datadogRum.setUser({
        id: user.id.toString(),
      });
      console.info('The user has been in Datadog RUM:', user.id);
    } catch (error) {
      console.error('Failed to set user in Datadog RUM:', error);
    }
    try {
      datadogLogs.setUser({
        id: user.id.toString(),
      });
      console.info();
    } catch {
      console.error('Failed to set user in Datadog Logs');
    }
  }

  const DEFAULT_QUERY_CONFIG = generateQueryConfig();

  // initialize new React Query instance
  const queryClient = new QueryClient(DEFAULT_QUERY_CONFIG);

  // initialise LD instance
  useEffect(() => {
    setLoading(true);
    LaunchDarkly.init(user ?? undefined).finally(() => {
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" sx={{ pl: { xs: 0, md: '300px' } }}>
        <CircularProgress size={30} />
      </Stack>
    );
  }

  return (
    <GoogleOAuthProvider clientId={settings.googleLoginClientID}>
      <BrandThemeProvider theme={alternaleaf}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <LaunchDarkly.Provider>
            <QueryClientProvider client={queryClient}>
              <TsrReactQueryProviders>
                <ReactQueryDevtools initialIsOpen={false} />
                <ModalProvider>
                  <TourProvider>
                    <AppRoutes />
                    <ToastContainer />
                    <AppSideEffects />
                  </TourProvider>
                </ModalProvider>
              </TsrReactQueryProviders>
            </QueryClientProvider>
          </LaunchDarkly.Provider>
        </LocalizationProvider>
      </BrandThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
